// Lo que el complemento de Chatbularis le pide a Word: leer la selección y
// escribir la respuesta en el documento.
//
// office.js no se empaqueta: Microsoft exige cargarlo desde su CDN, y es el que
// sabe hablar con cada Word (Windows, Mac, web). build/word ya lo trae en el
// <head> (ver scripts/pagina-word.js); en desarrollo, o si la página llegó sin
// él, se agrega acá.
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { soloElEscrito } from './limpiarEscrito';

/* Lo poco de office.js que se usa. Sin @types/office-js: son cuatro llamadas y
   no vale una dependencia. */
type RangoWord = {
  text: string;
  font: { name: string | null; size: number | null };
  load: (campos: string) => RangoWord;
  insertHtml: (html: string, donde: 'Replace' | 'After' | 'Start' | 'End') => RangoWord;
  select: () => void;
};
type ContextoWord = { document: { getSelection: () => RangoWord; body: RangoWord }; sync: () => Promise<void> };
declare global {
  interface Window {
    Office?: {
      onReady: () => Promise<{ host: string | null }>;
      context?: { document?: { addHandlerAsync: (evento: string, cb: () => void) => void } };
      EventType?: { DocumentSelectionChanged: string };
    };
    Word?: { run: <T>(fn: (ctx: ContextoWord) => Promise<T>) => Promise<T> };
  }
}

export const OFFICE_JS = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js';

let listo: Promise<boolean> | null = null;

/** Carga office.js si falta y espera a que Word conteste. Devuelve si la página
 *  está abierta dentro de Word: fuera de él (un navegador común) el chat
 *  funciona igual, pero no hay documento donde leer ni escribir. */
export function enWord(): Promise<boolean> {
  if (listo) return listo;
  listo = (async () => {
    if (!window.Office) {
      await new Promise<void>((ok, mal) => {
        const s = document.createElement('script');
        s.src = OFFICE_JS;
        s.onload = () => ok();
        s.onerror = () => mal(new Error('No se pudo cargar office.js'));
        document.head.appendChild(s);
      }).catch(() => undefined);
    }
    if (!window.Office) return false;
    // Fuera de Office, onReady igual resuelve, con host null. Se le pone un
    // plazo por si algún navegador lo deja colgado.
    const info = await Promise.race([
      window.Office.onReady(),
      new Promise<{ host: null }>((ok) => setTimeout(() => ok({ host: null }), 8000)),
    ]);
    return info.host === 'Word' && !!window.Word;
  })();
  return listo;
}

/** El texto seleccionado ahora en el documento, o '' si no hay nada. */
export async function leerSeleccion(): Promise<string> {
  if (!window.Word) return '';
  return window.Word.run(async (ctx) => {
    const r = ctx.document.getSelection();
    r.load('text');
    await ctx.sync();
    return (r.text || '').trim();
  });
}

/** Todo el texto del documento, sin formato. */
export async function leerDocumento(): Promise<string> {
  if (!window.Word) return '';
  return window.Word.run(async (ctx) => {
    const b = ctx.document.body;
    b.load('text');
    await ctx.sync();
    return (b.text || '').trim();
  });
}

/** Lo más que admite una consulta desde Word, sea la selección o el documento
 *  entero (unas 80 páginas). Espejo de MAX_TEXTO_WORD en
 *  backend/src/routes/asistente.ts: se revisa acá para avisar antes de
 *  mandar, sin cobrar nada. */
export const MAX_TEXTO_WORD = 250000;

/** Avisa cada vez que el abogado cambia la selección. Solo dentro de Word. */
export function alCambiarSeleccion(cb: () => void) {
  const o = window.Office;
  const doc = o?.context?.document;
  if (!doc || !o?.EventType) return;
  doc.addHandlerAsync(o.EventType.DocumentSelectionChanged, cb);
}

/** La respuesta como HTML para Word. Pasa por soloElEscrito —lo mismo que la
 *  descarga en .docx— para que un "aquí tienes" no quede pegado dentro de la
 *  demanda. Los títulos se bajan a párrafos en negrita: Word los convertiría
 *  en sus estilos "Título 1", en Calibri azul, que desentonan en un escrito. */
export function aHtmlParaWord(markdown: string): string {
  const html = marked.parse(soloElEscrito(markdown), { async: false }) as string;
  const sinTitulos = html.replace(/<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/g, '<p><strong>$1</strong></p>');
  return DOMPurify.sanitize(sinTitulos, { USE_PROFILES: { html: true } });
}

/** Escribe la respuesta en el documento.
 *
 *  - 'reemplazar': en lugar de lo seleccionado. Sin selección, en el cursor.
 *  - 'despues': a continuación de lo seleccionado, sin tocarlo.
 *
 *  Toma la letra y el tamaño del lugar donde se inserta: el HTML trae los de
 *  Word por omisión, y un párrafo en otra letra en medio del escrito se nota. */
export async function insertar(markdown: string, modo: 'reemplazar' | 'despues'): Promise<void> {
  if (!window.Word) throw new Error('Abre Chatbularis desde Word para insertar en el documento.');
  const html = aHtmlParaWord(markdown);
  await window.Word.run(async (ctx) => {
    const sel = ctx.document.getSelection();
    sel.load('font/name,font/size');
    await ctx.sync();
    const { name, size } = sel.font;
    const nuevo = sel.insertHtml(html, modo === 'reemplazar' ? 'Replace' : 'After');
    if (name) nuevo.font.name = name;
    if (size) nuevo.font.size = size;
    nuevo.select();
    await ctx.sync();
  });
}
